/* eslint-disable jsx-a11y/media-has-caption */
import React, { useEffect } from "react";

import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import Paper from "@mui/material/Paper";
import TableContainer from "@mui/material/TableContainer";
import TablePagination from "@mui/material/TablePagination";
import {
  Autocomplete,
  Popover,
  TextField,
  Stack,
  IconButton,
  Popper,
  CircularProgress,
  Box,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import ButtonView from "../../../common/Button";
import { dashboardService } from "../../services";
import TableView from "../../../common/Table";
import { Data } from "./types";
import EpisodeRow from "./parts/EpisodeRow";
import { bytesToSize } from "../../../../utils/bytesToSize";
import { Column } from "../../../common/Table/types";
import { formatDateAndTime } from "../../../../utils/formatDate";
import { ProjectsWithMetadata } from "../../../Project/services/projects.service";
import { useWebSocket } from "../../../../hooks/useWebSocket";
import { WebSocketEventName } from "../../../common/enums/WebSocketEventName";
import { EpisodeStatus } from "../../../Episode/enums/EpisodeStatus";
import { usersService } from "../../../common/Headerbar/services";
import { RoleName } from "../../../common/enums/RoleName";
import RoleBasedAccess from "../../../common/RoleBasedAccess";
import { useUserState } from "../../../../store";

const columns: Column[] = [
  { id: "name", label: "Episode", minWidth: 170 },
  { id: "project", label: "Project", minWidth: 100 },
  { id: "upload", label: "Upload Date", minWidth: 100 },
  { id: "file_type", label: "File Type", minWidth: 100 },
  { id: "file_size", label: "File Size", minWidth: 100 },
  { id: "status", label: "Status", minWidth: 100 },
  { id: "view", label: "Actions", minWidth: 100 },
];

function createData(
  id: string,
  name: string,
  project: string,
  upload: string,
  file_type: string,
  file_size: string,
  status: EpisodeStatus,
): Data {
  return { id, name, project, upload, file_type, file_size, status };
}

interface Props {
  projects: ProjectsWithMetadata[] | undefined;
  refetchProjects: () => Promise<any>;
}

// Custom Popper for Autocomplete dropdown
function StyledPopper(props: any) {
  return (
    <Popper
      {...props}
      modifiers={[{ name: "offset", options: { offset: [0, 6] } }]}
      style={{ zIndex: 1301 }}
    />
  );
}

// Custom Paper for dropdown menu
function StyledPaper(props: any) {
  return (
    <Paper
      elevation={3}
      sx={{
        borderRadius: 3,
        boxShadow: "0 8px 32px rgba(0,0,0,0.10)",
        mt: 1,
        minWidth: 180,
        p: 0.5,
        background: "#fff",
      }}
      {...props}
    />
  );
}

export default function EpisodeList({ projects, refetchProjects }: Props) {
  const { user } = useUserState();
  const navigate = useNavigate();
  const [page, setPage] = React.useState(1);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [isRefreshing, setIsRefreshing] = React.useState<boolean>(false);
  const socket = useWebSocket();

  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(
    null,
  );
  const [selectedVideo, setSelectedVideo] = React.useState("");
  const [selectedProject, setSelectedProject] = React.useState<{
    id: string;
    label: string;
  } | null>(null);

  const handleClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    value: string,
  ) => {
    setAnchorEl(event.currentTarget);
    setSelectedVideo(value);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;

  const { data, refetch, isLoading } = useQuery({
    queryKey: ["episodes", selectedProject?.id || "", page, rowsPerPage],
    queryFn: () =>
      dashboardService.fetchEpisodes({
        projectId: selectedProject?.id || "",
        page,
        perPage: rowsPerPage,
      }),
  });

  const { refetch: refetchDashboard } = useQuery({
    queryKey: ["dashboard-stats"],
    queryFn: () => dashboardService.fetchDashboardStats(),
  });

  const { refetch: refetchAuthenticatedUser } = useQuery({
    queryKey: ["authenticated-user"],
    queryFn: () => usersService.fetchAuthenticardUser(),
  });

  const handleRefreshEpisode = async () => {
    setIsRefreshing(true);
    try {
      const startTime = Date.now();
      await Promise.all([
        refetch(),
        refetchDashboard(),
        refetchAuthenticatedUser(),
      ]);

      // Ensure minimum delay of 500ms for better UX
      const elapsedTime = Date.now() - startTime;
      const minDelay = 500;
      if (elapsedTime < minDelay) {
        await new Promise<void>((resolve) => {
          setTimeout(() => resolve(), minDelay - elapsedTime);
        });
      }
    } finally {
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    if (socket) {
      socket.on(WebSocketEventName.EPISODE_INSIGHTS_GENERATED, async () => {
        await refetch();
        await refetchDashboard();
        await refetchAuthenticatedUser();
      });
    }

    return () => {
      if (socket) {
        socket.off(WebSocketEventName.EPISODE_INSIGHTS_GENERATED);
      }
    };
  }, [socket]);

  useEffect(() => {
    if (socket) {
      socket.on(WebSocketEventName.EPISODE_GENERATED, async () => {
        await refetch();
        await refetchDashboard();
        await refetchAuthenticatedUser();
      });
    }

    return () => {
      if (socket) {
        socket.off(WebSocketEventName.EPISODE_GENERATED);
      }
    };
  }, [socket]);

  useEffect(() => {
    if (socket) {
      socket.on(WebSocketEventName.EPISODE_PROCESSING_FAILED, async () => {
        await refetch();
        await refetchDashboard();
        await refetchAuthenticatedUser();
      });
    }

    return () => {
      if (socket) {
        socket.off(WebSocketEventName.EPISODE_PROCESSING_FAILED);
      }
    };
  }, [socket]);

  const rows =
    data?.episodes.map((episode) => {
      return createData(
        episode.id,
        episode.name,
        episode.project.name,
        formatDateAndTime(episode.upload.created_at),
        episode.upload.mime_type,
        bytesToSize(parseFloat(episode.upload.size)),
        episode.status,
      );
    }) || [];

  const handleChangePage = (_event: unknown, newPage: number) => {
    if (newPage === 0) {
      setPage(newPage + 1);
    }

    setPage(newPage + 1);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(+event.target.value);
    setPage(1);
  };

  const handleProjectChange = (
    _event: React.SyntheticEvent,
    newValue: { id: string; label: string } | null,
  ) => {
    if (!newValue) {
      setSelectedProject(null);
    } else {
      setSelectedProject(newValue);
    }
  };

  return (
    <>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        <video src={selectedVideo} width="450" height="300" autoPlay controls />
      </Popover>
      <Paper
        elevation={2}
        sx={{
          mb: 2,
          px: { xs: 1, sm: 2 },
          py: { xs: 1, sm: 2 },
          borderRadius: 3,
          boxShadow: "0 2px 8px rgba(0,0,0,0.07)",
          display: "flex",
          alignItems: "center",
          width: "100%",
        }}
      >
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          justifyContent={{ xs: "center", sm: "space-between" }}
          sx={{
            width: "100%",
            flexWrap: "nowrap",
            overflowX: "auto",
            minWidth: 0,
          }}
        >
          <Autocomplete
            disablePortal
            clearOnEscape
            onChange={handleProjectChange}
            value={selectedProject}
            onOpen={() => {
              // Refetch projects when dropdown is opened to get latest data
              refetchProjects();
            }}
            options={
              projects && projects.length
                ? projects.map((project) => {
                    return { label: project.name, id: project.id };
                  })
                : []
            }
            PopperComponent={StyledPopper}
            PaperComponent={StyledPaper}
            sx={{
              mt: 2,
              width: { xs: 180, sm: 240 },
              minWidth: 0,
              borderRadius: 3,
              py: 1,
              "& .MuiOutlinedInput-root": {
                borderRadius: 3,
                fontSize: 15,
                background: "#fff",
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#1976d2", // custom focus color
                  boxShadow: "0 0 0 2px #e3e8ef",
                },
              },
              "& .MuiAutocomplete-option": {
                fontSize: 15,
                paddingY: 1.2,
                paddingX: 2,
                borderRadius: 2,
                transition: "background 0.15s",
                '&[aria-selected="true"]': {
                  background: "#f0f4fa",
                  color: "#1976d2",
                },
                "&:hover": {
                  background: "#f5f7fa",
                },
              },
              "& .MuiAutocomplete-listbox": {
                borderRadius: 3,
                boxShadow: "0 8px 32px rgba(0,0,0,0.10)",
                p: 0.5,
                background: "#fff",
              },
              "& .MuiInputLabel-root": {
                fontWeight: 600,
                color: "#23272E",
                fontSize: 14,
              },
              "& .Mui-focused .MuiInputLabel-root": {
                color: "#1976d2",
              },
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                label="Filter By Project"
              />
            )}
            isOptionEqualToValue={(option, value) => option.id === value.id}
          />
          <Stack direction="row" spacing={1} alignItems="center">
            <IconButton
              onClick={handleRefreshEpisode}
              size="small"
              disabled={isRefreshing}
              sx={{
                color: "primary.main",
                background: "#f5f7fa",
                borderRadius: "50%",
                width: 36,
                height: 36,
                boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
                "&:hover": { background: "#e3e8ef" },
                "&:disabled": {
                  background: "#f0f0f0",
                  color: "#999",
                },
                ml: { xs: 0, sm: 1 },
              }}
            >
              {isRefreshing ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <RefreshIcon sx={{ fontSize: 20 }} />
              )}
            </IconButton>
            <RoleBasedAccess allowedRoles={[RoleName.ADMIN, RoleName.EDITOR]}>
              <ButtonView
                type="button"
                variant="contained"
                size="small"
                label="Add Episode"
                disabled={
                  user?.organization?.subscription?.available_credits === 0
                }
                startIcon={<AddOutlinedIcon sx={{ fontSize: 12 }} />}
                handleOnClick={() => {
                  navigate("/episodes/upload");
                }}
                sx={{
                  borderRadius: 999,
                  px: 1.5,
                  py: 0.5,
                  fontSize: 13,
                  height: 36,
                  boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
                  textTransform: "none",
                  fontWeight: 600,
                  letterSpacing: 0.2,
                }}
              />
            </RoleBasedAccess>
          </Stack>
        </Stack>
      </Paper>

      {/* Modern card-like table container with section title */}
      <Paper
        elevation={2}
        sx={{
          p: { xs: 1.5, sm: 3 },
          borderRadius: 4,
          boxShadow: "0 4px 24px rgba(0,0,0,0.07)",
          mb: 2,
        }}
      >
        {isLoading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 400,
              flexDirection: "column",
              gap: 2,
            }}
          >
            <CircularProgress size={40} />
            <Typography variant="body2" color="text.secondary">
              Loading episodes...
            </Typography>
          </Box>
        ) : (
          <>
            <TableContainer>
              <TableView
                columns={columns}
                rows={rows}
                handleClick={handleClick}
                TableList={EpisodeRow}
                handleCustomEvent={() => {}}
              />
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[10, 25, 100]}
              component="div"
              count={data?.pagination.total_items || 0}
              rowsPerPage={rowsPerPage}
              page={page - 1}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              showFirstButton
              showLastButton
            />
          </>
        )}
      </Paper>
    </>
  );
}
